import { ThaiMeaningFullProps } from 'core/interfaces/dictionary-thai-interfaces'

// A linked-sense group as the word page renders it: the primary sense (first
// member in display order — the worker pools the group's compounds and
// classified sentences onto it) plus its sibling senses, which render as
// compact sub-lines inside the primary's block.
export type ThaiSenseGroup = {
   primary: ThaiMeaningFullProps
   siblings: ThaiMeaningFullProps[]
}

// Fold a word's display-order-sorted meanings into sense groups, keyed by
// senseGroupId (ungrouped senses are their own singleton group). Group position
// follows its first member. Degrades to one group per sense on data from a
// worker that predates senseGroupId.
export const groupThaiMeanings = (meanings: ThaiMeaningFullProps[]): ThaiSenseGroup[] => {
   const groups: ThaiSenseGroup[] = []
   const groupByKey = new Map<number, ThaiSenseGroup>()
   for (const meaning of meanings) {
      const key = meaning.senseGroupId
      if (key === null || key === undefined) {
         groups.push({ primary: meaning, siblings: [] })
         continue
      }
      const existing = groupByKey.get(key)
      if (existing) {
         existing.siblings.push(meaning)
      } else {
         const group: ThaiSenseGroup = { primary: meaning, siblings: [] }
         groupByKey.set(key, group)
         groups.push(group)
      }
   }
   return groups
}

// The meanings a sentence token narrowed to `senseId` should show: the whole
// linked group when the matched sense has one, the single sense otherwise, and
// every sense when the id isn't among the meanings (never empty a tooltip).
export const narrowMeaningsToSenseGroup = <T extends { id: number; senseGroupId?: number | null }>(
   meanings: T[],
   senseId: number | null | undefined
): T[] => {
   if (senseId == null) return meanings
   const matched = meanings.find((m) => m.id === senseId)
   if (!matched) return meanings
   if (matched.senseGroupId == null) return [matched]
   return meanings.filter((m) => m.senseGroupId === matched.senseGroupId)
}
